import { ApolloClient, gql } from '@apollo/client/core';
import type {
  DeleteVenueMutationInput,
  Mutation,
  Query,
  SaveVenueMutationInput,
} from '../../../generated/api.graphql';
import * as VPErrors from '../../../VPError';
import type { VPResult } from '../../VPResult';
import { VP_ERROR_FIELDS } from '../../VPError/fragments';
import * as Venue from './Venue';
import * as VenueInput from './VenueInput';
import { VENUE_FIELDS } from './fragments';

/**
 * Venue administration on the tenant (AppSchema) connection. The same three fields exist on the
 * legacy schema nested under `app { … }`; `venues` is a plain list on both, with no paginator of
 * either kind.
 *
 * **Nothing here catches.** A transport failure throws and propagates; `VPResult`'s
 * `success: false` covers only the GraphQL-level outcomes — an absent payload, a populated
 * `errors` list. That matches `App/Event` and `App/TeamRole` and preserves the legacy store's
 * `try`/`finally`-with-no-`catch`. This is a transport move, not a change of failure semantics.
 */

const failure = (message: string): VPResult<never, VPErrors.VPError[]> => ({
  success: false,
  errors: [{ path: '', message }],
});

export const ALL_VENUES_QUERY = gql`
  query AppAllVenues {
    venues {
      ...VenueFields
    }
  }
  ${VENUE_FIELDS}
`;

export const SAVE_VENUE_MUTATION = gql`
  mutation saveVenue($input: SaveVenueMutationInput!) {
    saveVenue(input: $input) {
      venue {
        ...VenueFields
      }
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VENUE_FIELDS}
  ${VP_ERROR_FIELDS}
`;

export const DELETE_VENUE_MUTATION = gql`
  mutation deleteVenue($input: DeleteVenueMutationInput!) {
    deleteVenue(input: $input) {
      success
      errors {
        ...VPErrorFields
      }
    }
  }
  ${VP_ERROR_FIELDS}
`;

export const fetchVenues = async (client: ApolloClient<Query>): Promise<Venue.Venue[]> => {
  const { data } = await client.query<Query>({
    query: ALL_VENUES_QUERY,
    fetchPolicy: 'network-only',
  });

  return (data.venues ?? []).map(Venue.fromGql);
};

/**
 * Creates when the input carries no `id`, updates when it does.
 *
 * The payload selection reuses `VenueFields`, so it drops the `logoUrl` the legacy mutation asked
 * for — nothing ever read it — and picks up `defaultSet`, which the legacy selection lacked.
 */
export const saveVenue = async (
  client: ApolloClient<Mutation>,
  venue: VenueInput.VenueInput,
): Promise<VPResult<Venue.Venue, VPErrors.VPError[]>> => {
  // Typed rather than inlined: the `mutate<Mutation>` generic does not check `variables`.
  const input: SaveVenueMutationInput = { venue: VenueInput.toGqlInput(venue) };

  const { data } = await client.mutate<Mutation>({
    mutation: SAVE_VENUE_MUTATION,
    variables: { input },
  });

  const payload = data?.saveVenue;
  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  if (errors.length) {
    return { success: false, errors };
  }

  // This payload carries no `success` — the legacy one had none either — so an empty error list
  // with no entity is the one shape that would otherwise read as a silent success.
  if (!payload?.venue) {
    return failure('Failed to save the venue.');
  }

  return { success: true, data: Venue.fromGql(payload.venue) };
};

export const deleteVenue = async (
  client: ApolloClient<Mutation>,
  id: number,
): Promise<VPResult<null, VPErrors.VPError[]>> => {
  // The argument is `ID!`, which codegen types as a string, even though every caller holds the
  // venue id as a number.
  const input: DeleteVenueMutationInput = { id: String(id) };

  const { data } = await client.mutate<Mutation>({
    mutation: DELETE_VENUE_MUTATION,
    variables: { input },
  });

  const payload = data?.deleteVenue;

  if (payload?.success) {
    return { success: true, data: null };
  }

  const errors = VPErrors.fromGqlCollection(payload?.errors ?? []);

  // A failure the caller cannot render is worse than a generic message.
  return errors.length ? { success: false, errors } : failure('Failed to delete the venue.');
};
